<script setup>
import {onMounted, onUnmounted, reactive} from "vue";
import countdown from '@/StandaloneJS/vue-countdown';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
const compName = 'TasksPendingDashComponent';

const props = defineProps({
  contextSchemaA: String
});

onMounted(() => {
  getData();

  activeComponentStore.registerComponent(
      compName,
      [
        'tasks'
      ],
      getData
  );
});

onUnmounted(() => {
  activeComponentStore.deregisterComponent(compName);
});

const data = reactive({
  dataLoading: true,
  date: '',
  headers: [
    {title: 'Claim Number', key: 'claimnumber'},
    {title: 'Insured', key: 'insuredname'},
    {title: 'Client', key: 'clientsname'},
    {title: 'Adjuster', key: 'adjuster'},
    {title: 'Claim Age (Days)', key: 'age'},
    {title: 'Time Remaining', key: 'timedue'},
    {title: 'Pending Task', key: 'name'},
    {title: 'Appointment', key: 'formattedDateTime'},

  ],
  items: [],
  loading: false,
  newAdj: false,
  search: null,
  showOnlyMostUrgent: false
});

const getData = () => {
  data.dataLoading = true;
  ApiWrapper.post('tasks-pending','list-tasks-pending-dash',{
    showOnlyMostUrgent: data.showOnlyMostUrgent
  })
      .then((response) => {
        if(response.response) {
          data.items = response.response;
          for (const x in data.items) {
            if (data.items[x].appointmentdate === null || data.items[x].appointmenttime === null || data.items[x].appointmentdate === "") {
              data.items[x].formattedDateTime = 'Not Set';
            } else {
              // *** Code to get timezone to display
              let newTimeStamp = `${data.items[x].appointmentdate} ${data.items[x].appointmenttime}`;
              data.items[x].formattedDateTime = formatTimestampByLocale(newTimeStamp);
            }
          }
        }
      })
      .catch((error) => {
        ToastUtil.error(error);
      })
      .finally(() => {
        data.dataLoading = false;
      })
}

const formatTime = (time) => {
  time = time
      .toString()
      .match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time]
  if (time.length > 1) {
    time = time.slice(1)
    time[5] = +time[0] < 12 ? ' AM' : ' PM'
    time[0] = +time[0] % 12 || 12
  }
  return time.join('')
}

const formatISODateToAmerican = (date) => {
  if (!date) return null
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year}`
}

const formatTimestamp = (date, time) => {
  let formattedDate = formatISODateToAmerican(date)
  let formattedTime = formatTime(time)

  return formattedDate + ' @ ' + formattedTime
}

const formatTimestampByLocale = (timestamp) => {
  const date = new Date(timestamp)
  const timeOptions = {
    hour12: true,
    hour: "numeric",
    minute: "2-digit",
  }
  const dateOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZoneName: 'short'
  }
  return date.toLocaleString('en', {...dateOptions, ...timeOptions})
};

const GetIsFireType = (claim) => {
  if(claim.claimtype) {
    return claim.claimtype.toString().toLowerCase().includes('fire')
  }
  return false;
};

</script>

<template>
  <v-container fluid id="entreos-resize-target">
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Tasks Pending</v-toolbar-title>

        <v-divider class="mx-4" inset vertical></v-divider>
        <v-spacer></v-spacer>

        <v-switch v-model="data.showOnlyMostUrgent" label="show most urgent task for claim" color="info" value="info"
                  hide-details @update:modelValue="data.showOnlyMostUrgent=$event;getData()"></v-switch>
      </v-toolbar>
      <v-row>
        <v-col cols="4">
          <v-text-field v-model="data.search" append-inner-icon="mdi-magnify" label="Search" single-line hide-details
                        class="ml-4"></v-text-field>
        </v-col>
        <v-col>
          <v-btn icon variant="flat" justify-content="center" class="mt-1" @click="getData()">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
          :items="data.items"
          :headers="data.headers"
          item-value="tasksid"
          :search="data.search"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait"
          :items-per-page-options="[10, 25, 50, 100, -1]"
          density="compact"
      >
        <template v-slot:[`item.claimnumber`]="{ item }">
          <RouterLink :to="`/claim/${item.claimid}`">
            {{ item.claimnumber }}
          </RouterLink>
          <span class="ma-ml-3" v-if="GetIsFireType(item)"> <v-icon color="orange">mdi-fire-circle</v-icon> </span>
        </template>
        <template v-slot:[`item.personname`]="{ item }">
          <RouterLink :to="`/userprofile/${item.personid}`">
            {{ item.personname }}
          </RouterLink>
        </template>
        <template v-slot:[`item.clientsname`]="{ item }">
          <RouterLink :to="`/client/${item.clientsid}`" v-if="Permissions.checkPermissions(['clients.read'])">
            {{ item.clientsname }}
          </RouterLink>
          <span v-else>{{ item.clientsname }}</span>
        </template>
        <template v-slot:[`item.timedue`]="{item}">
          <v-chip label v-if="new Date(item.timedue) - new Date() > 0" class="success text--white">
            <v-icon dark left>mdi-calendar</v-icon>
            <countdown :time="new Date(item.timedue) - new Date()">
              <template v-slot="props">
                {{ props.totalHours.toString().padStart(2, 0) }}:{{
                  props.minutes.toString().padStart(2, 0)
                }}:{{
                  props.seconds.toString().padStart(2, 0)
                }}
              </template>
            </countdown>
          </v-chip>
          <v-chip label v-else-if="!item.timecompleted" class="error text--white">
            <v-icon dark left>mdi-calendar</v-icon>
            <countdown :time="new Date() - new Date(item.timedue)" :interval="-1000">
              <template v-slot="props">
                {{ props.totalHours.toString().padStart(2, 0) }}:{{
                  props.minutes.toString().padStart(2, 0)
                }}:{{
                  props.seconds.toString().padStart(2, 0)
                }}
              </template>
            </countdown>
          </v-chip>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>